import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { ThemeConfig as NewThemeConfig, TenantThemeOverrides } from '@/shared/theme/types'
import { resolveTheme, resolveThemeFromConfig, resolveDarkTheme, getLightTokens } from '@/shared/theme/themeResolver'
import { applyThemeToDOM } from '@/shared/theme/cssVariableMapper'
import { loadFont } from '@/shared/theme/fontLoader'
import { IconProvider } from '@/shared/icons'

const THEME_STORAGE_KEY = 'troia-theme'

type ThemeMode = 'light' | 'dark'

interface ThemeContextType {
  theme: ThemeMode
  isDark: boolean
  toggleTheme: () => void
  setTheme: (mode: ThemeMode) => void
  themeConfig: NewThemeConfig
  lightTokens: NewThemeConfig
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

function getStoredTheme(): ThemeMode {
  if (typeof window === 'undefined') return 'light'
  const stored = localStorage.getItem(THEME_STORAGE_KEY)
  if (stored === 'light' || stored === 'dark') return stored
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

interface ThemeProviderProps {
  children: ReactNode
  baseThemeId?: string
  appOverrides?: TenantThemeOverrides
  companyOverrides?: TenantThemeOverrides
  defaultTheme?: ThemeMode
  /** Quando definido, ignora localStorage e força o modo especificado (útil para login) */
  forceMode?: ThemeMode
}

export function ThemeProvider({ children, baseThemeId, appOverrides, companyOverrides, defaultTheme = 'light', forceMode }: ThemeProviderProps) {
  const [mode, setMode] = useState<ThemeMode>(() => forceMode ?? getStoredTheme() ?? defaultTheme)

  const resolvedTheme = useMemo(() => {
    const appResolved = resolveTheme(appOverrides, baseThemeId ?? 'apple')
    return resolveThemeFromConfig(companyOverrides, appResolved)
  }, [baseThemeId, appOverrides, companyOverrides])

  const activeTheme = useMemo(() =>
    mode === 'dark' ? resolveDarkTheme(resolvedTheme) : resolvedTheme,
    [mode, resolvedTheme]
  )

  const lightTokens = useMemo(() => getLightTokens(resolvedTheme), [resolvedTheme])

  const isDark = mode === 'dark'

  const toggleTheme = () => {
    if (forceMode) return
    const newMode = mode === 'light' ? 'dark' : 'light'
    setMode(newMode)
    localStorage.setItem(THEME_STORAGE_KEY, newMode)
  }

  const setTheme = (newMode: ThemeMode) => {
    if (forceMode) return
    setMode(newMode)
    localStorage.setItem(THEME_STORAGE_KEY, newMode)
  }

  // Sincronizar com forceMode quando mudar
  useEffect(() => {
    if (forceMode) setMode(forceMode)
  }, [forceMode])

  useEffect(() => {
    applyThemeToDOM(activeTheme, isDark)
    loadFont(activeTheme.typography.fontFamily)
    document.documentElement.classList.toggle('dark', isDark)
  }, [activeTheme, isDark])

  // Listen for system theme changes (desabilitado quando forceMode ativo)
  useEffect(() => {
    if (forceMode) return
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (e: MediaQueryListEvent) => {
      const stored = localStorage.getItem(THEME_STORAGE_KEY)
      if (!stored) {
        setMode(e.matches ? 'dark' : 'light')
      }
    }
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [forceMode])

  const value: ThemeContextType = {
    theme: mode,
    isDark,
    toggleTheme,
    setTheme,
    themeConfig: activeTheme,
    lightTokens,
  }

  return (
    <ThemeContext.Provider value={value}>
      <IconProvider
        packId={activeTheme.icons.pack}
        fallbackPackId={activeTheme.icons.fallbackPack}
        strokeWidth={activeTheme.icons.strokeWidth}
      >
        {children}
      </IconProvider>
    </ThemeContext.Provider>
  )
}

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
